import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  FileText,
  Search,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useResizableSidebar } from '@/hooks/useResizableSidebar';
import { cn } from '@/lib/utils';
import { useGetFirmQuery } from '@/services/api/firmsService';
import {
  extractSearchMatches,
  highlightMatches,
  SearchMatch,
  SearchResults,
} from '@/utils/searchUtils';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DocumentsPanel } from './DocumentsPanel';
import { SearchResultsList } from './SearchResultsList';

type SidebarTab = 'guidelines' | 'documents';

interface FirmGuidelinesSidebarProps {
  firmId: string | null;
  intakeId?: string | null;
  matterId?: string | null;
  onSendEngagementLetter?: (options?: { cancelPrevious?: boolean }) => void;
}

export const FirmGuidelinesSidebar: React.FC<FirmGuidelinesSidebarProps> = ({
  firmId,
  intakeId,
  matterId,
  onSendEngagementLetter,
}) => {
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<SidebarTab>('guidelines');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResults | null>(
    null,
  );
  const [activeMatchIndex, setActiveMatchIndex] = useState<number | null>(null);
  const [expandedSectionIndex, setExpandedSectionIndex] = useState<
    number | null
  >(null);

  const { data: firm, isLoading } = useGetFirmQuery(firmId || '', {
    skip: !firmId,
  });

 



  const sections = useMemo(
    () => firm?.intake_preferences?.sections || [],
    [firm?.intake_preferences?.sections],
  );
  const sectionCount = sections.length;

  // Add resize functionality with auto-collapse
  const { width, isResizing, handleMouseDown, restoreWidth } =
    useResizableSidebar({
      minWidth: 250,
      maxWidth: 400,
      defaultWidth: 320,
      collapseThreshold: 200,
      onCollapse: () => setIsCollapsed(true),
    });

  // Handle expanding sidebar (restore width and expand)
  const handleExpand = useCallback(() => {
    restoreWidth();
    setIsCollapsed(false);
  }, [restoreWidth]);

  // Handle drag-to-expand from collapsed state
  const [isDraggingCollapsed, setIsDraggingCollapsed] = useState(false);
  const collapsedStartXRef = useRef(0);

  const handleCollapsedMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDraggingCollapsed(true);
    collapsedStartXRef.current = e.clientX;
  }, []);

  useEffect(() => {
    if (!isDraggingCollapsed) {
      return undefined;
    }

    let hasExpanded = false;

    const handleMouseMove = (e: MouseEvent) => {
      const delta = collapsedStartXRef.current - e.clientX;

      // Expand immediately if dragged left by at least 100px
      if (delta > 100 && !hasExpanded) {
        hasExpanded = true;
        restoreWidth();
        setIsCollapsed(false);
        setIsDraggingCollapsed(false);
        document.body.style.cursor = '';
        return;
      }

      // Show visual feedback if dragged left enough
      if (delta > 50) {
        // User is dragging left to expand
        document.body.style.cursor = 'col-resize';
      }
    };

    const handleMouseUp = () => {
      setIsDraggingCollapsed(false);
      document.body.style.cursor = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDraggingCollapsed, restoreWidth]);

  // Extract search matches
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setActiveMatchIndex(null);
      setExpandedSectionIndex(null);
      return;
    }

    const results = extractSearchMatches(sections, searchQuery);
    setSearchResults(results);

    // Auto-select first match
    if (results.matches.length > 0) {
      setActiveMatchIndex(0);
    }
  }, [searchQuery, sections]);

  // Handle match click - expand section and scroll to match
  const handleMatchClick = useCallback((match: SearchMatch) => {
    setActiveMatchIndex(match.globalMatchIndex);
    setExpandedSectionIndex(match.sectionIndex);

    // Scroll to the specific active match after render
    setTimeout(() => {
      // Find the active match element
      const activeMatchElement = document.querySelector(
        '[data-is-active="true"]',
      );

      if (activeMatchElement) {
        activeMatchElement.scrollIntoView({
          behavior: 'auto',
          block: 'center',
        });
      }
    }, 100);
  }, []);

  // Navigate between matches
  const goToNextMatch = useCallback(() => {
    if (!searchResults || searchResults.matches.length === 0) return;

    const nextIndex =
      activeMatchIndex !== null
        ? (activeMatchIndex + 1) % searchResults.matches.length
        : 0;

    const nextMatch = searchResults.matches[nextIndex];
    setActiveMatchIndex(nextIndex);

    // If in search results view (not expanded), scroll to the result card
    if (expandedSectionIndex === null) {
      setTimeout(() => {
        const resultCard = document.querySelector(
          `[data-match-index="${nextIndex}"]`,
        );
        if (resultCard) {
          resultCard.scrollIntoView({
            behavior: 'auto',
            block: 'center',
          });
        }
      }, 50);
    } else {
      // If in expanded view, scroll to the match in content
      handleMatchClick(nextMatch);
    }
  }, [activeMatchIndex, searchResults, expandedSectionIndex, handleMatchClick]);

  const goToPreviousMatch = useCallback(() => {
    if (!searchResults || searchResults.matches.length === 0) return;

    const prevIndex =
      activeMatchIndex !== null
        ? (activeMatchIndex - 1 + searchResults.matches.length) %
          searchResults.matches.length
        : searchResults.matches.length - 1;

    const prevMatch = searchResults.matches[prevIndex];
    setActiveMatchIndex(prevIndex);

    // If in search results view (not expanded), scroll to the result card
    if (expandedSectionIndex === null) {
      setTimeout(() => {
        const resultCard = document.querySelector(
          `[data-match-index="${prevIndex}"]`,
        );
        if (resultCard) {
          resultCard.scrollIntoView({
            behavior: 'auto',
            block: 'center',
          });
        }
      }, 50);
    } else {
      // If in expanded view, scroll to the match in content
      handleMatchClick(prevMatch);
    }
  }, [activeMatchIndex, searchResults, expandedSectionIndex, handleMatchClick]);

  if (!firmId) {
    return null;
  }

  if (isCollapsed) {
    return (
      <div className="relative w-12 bg-white border-l-4 border-l-slate-300 flex flex-col items-center py-3 flex-shrink-0 h-full">
        {/* Drag handle overlay for expand gesture - covers full border width */}
        <button
          type="button"
          className={cn(
            'absolute -left-2 top-0 bottom-0 w-3 z-20 border-0 p-0 m-0 bg-transparent',
            'hover:bg-blue-400/30 active:bg-blue-500/50',
            'transition-colors duration-150',
            isDraggingCollapsed && 'bg-blue-500/50',
          )}
          style={{ cursor: 'w-resize', outline: 'none' }}
          onMouseDown={handleCollapsedMouseDown}
          onClick={handleExpand}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleExpand();
            }
          }}
          aria-label="Click or drag left to expand sidebar"
        />

        <Button
          variant="ghost"
          size="icon"
          onClick={handleExpand}
          className="h-7 w-7 z-10"
          aria-label="Expand guidelines"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </Button>
        <div className="mt-3 text-[10px] text-black font-semibold [writing-mode:vertical-lr] rotate-180 z-10">
          {activeTab === 'guidelines' ? 'Guidelines' : 'Documents'}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative bg-white border-l-4 border-l-slate-300 flex flex-col flex-shrink-0 h-full shadow-lg',
        isResizing && 'select-none',
      )}
      style={{ width: `${width}px` }}
    >
      {/* Resize Handle - Left Border */}
      <button
        type="button"
        className={cn(
          'absolute -left-2 top-0 bottom-0 w-3 z-20 border-0 p-0 m-0 bg-transparent',
          'hover:bg-blue-400/30 active:bg-blue-500/50',
          'transition-colors duration-150 group',
          isResizing && 'bg-blue-500/50',
        )}
        style={{ cursor: 'col-resize', outline: 'none' }}
        onMouseDown={handleMouseDown}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            // Arrow left increases width (sidebar on right side)
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            // Arrow right decreases width
          }
        }}
        aria-label="Resize sidebar. Use arrow keys to adjust width."
      />

      {/* Header with match counter */}
      <div className="sticky top-0 z-10 border-b-2 border-slate-200 bg-gradient-to-r from-slate-50 to-blue-50">
        <div className="flex items-center justify-between px-3 py-2.5">
          <div className="flex-1">
            <h3 className="text-sm font-bold text-gray-900">
              {activeTab === 'guidelines'
                ? `${firm?.name || 'Firm'} Guidelines`
                : 'Documents'}
            </h3>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {activeTab === 'guidelines' && firm?.lead_system_link && (
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-[10px] gap-1 px-2"
                onClick={() => window.open(firm?.lead_system_link ?? '', '_blank')}
              >
                <ExternalLink className="h-3 w-3" />
                CMS
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsCollapsed(true)}
              className="h-7 w-7"
              aria-label="Collapse sidebar"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Tab switcher - only show when matter exists */}
        {matterId && (
          <div className="flex px-3 pb-0">
            <button
              type="button"
              onClick={() => setActiveTab('guidelines')}
              className={cn(
                'flex-1 text-[10px] font-semibold py-1.5 border-b-2 transition-colors',
                activeTab === 'guidelines'
                  ? 'text-blue-700 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700',
              )}
            >
              Guidelines
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('documents')}
              className={cn(
                'flex-1 text-[10px] font-semibold py-1.5 border-b-2 transition-colors flex items-center justify-center gap-1',
                activeTab === 'documents'
                  ? 'text-blue-700 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700',
              )}
            >
              <FileText className="h-3 w-3" />
              Documents
            </button>
          </div>
        )}
      </div>

      {/* Documents tab content */}
      {activeTab === 'documents' && matterId && (
        <div className="flex-1 min-h-0">
          <DocumentsPanel matterId={matterId} />
        </div>
      )}

      {/* Guidelines tab content */}
      {activeTab === 'guidelines' && (
        <>
      {/* Search bar with navigation */}
      <div className="sticky top-[52px] z-10 px-3 py-2 bg-white border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search guidelines..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (e.shiftKey) {
                  goToPreviousMatch();
                } else {
                  goToNextMatch();
                }
              }
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                goToNextMatch();
              }
              if (e.key === 'ArrowUp') {
                e.preventDefault();
                goToPreviousMatch();
              }
              if (e.key === 'Escape') {
                setSearchQuery('');
              }
            }}
            className="pl-8 pr-16 h-8 text-[11px] font-semibold placeholder:text-[11px]"
          />

          {/* Match navigation buttons */}
          {searchResults && searchResults.totalMatches > 0 && (
            <div className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center gap-0.5">
              <span className="text-[10px] text-gray-500 mr-0.5">
                {activeMatchIndex !== null ? activeMatchIndex + 1 : 0}/
                {searchResults.totalMatches}
              </span>
              <Button
                size="sm"
                variant="ghost"
                onClick={goToPreviousMatch}
                className="h-5 w-5 p-0"
              >
                <ChevronUp className="h-2.5 w-2.5" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={goToNextMatch}
                className="h-5 w-5 p-0"
              >
                <ChevronDown className="h-2.5 w-2.5" />
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Content: Search Results OR Normal Accordion */}
      <div className="flex-1 overflow-y-auto px-3 py-3 scroll-smooth">
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-6 h-6 border-4 border-amber-200 border-t-amber-600 rounded-full animate-spin mb-3" />
            <p className="text-xs text-gray-600">Loading guidelines...</p>
          </div>
        )}

        {!isLoading && sectionCount === 0 && (
          <div className="text-center py-8 px-6">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
              <Search className="w-6 h-6 text-slate-600" />
            </div>
            <p className="text-xs font-medium text-gray-900 mb-1.5">
              No guidelines configured
            </p>
            <p className="text-[10px] text-gray-500 mb-4">
              Configure intake preferences to add guidelines for this firm.
            </p>
            <Button
              variant="default"
              size="sm"
              onClick={() => navigate(`/firms/${firmId}/intake-preferences`)}
              className="bg-blue-600 hover:bg-blue-700 text-white text-[10px]"
            >
              Configure Guidelines
            </Button>
          </div>
        )}

        {!isLoading && sectionCount > 0 && searchResults ? (
          // SEARCH MODE: Show results with context snippets
          <>
            <div className="mb-2 flex items-center justify-between">
              {expandedSectionIndex !== null && (
                <button
                  type="button"
                  onClick={() => setExpandedSectionIndex(null)}
                  className="text-[10px] text-gray-500 hover:text-gray-700 flex items-center gap-0.5"
                >
                  <ChevronLeft className="w-2.5 h-2.5" />
                  Back to results
                </button>
              )}

              <button
                type="button"
                onClick={() => setSearchQuery('')}
                className="text-[10px] text-blue-600 hover:text-blue-700 font-medium flex items-center gap-0.5 ml-auto"
              >
                <X className="w-2.5 h-2.5" />
                Clear search
              </button>
            </div>

            {expandedSectionIndex === null ? (
              // Show search results list
              <SearchResultsList
                results={searchResults}
                onMatchClick={handleMatchClick}
                activeMatchIndex={activeMatchIndex}
              />
            ) : (
              // Show expanded section with highlights
              <div
                className="space-y-3"
                data-section-index={expandedSectionIndex}
              >
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
                  <h4 className="text-xs font-semibold text-gray-900 mb-1.5">
                    {sections[expandedSectionIndex].title}
                  </h4>
                  <div className="text-xs text-gray-700 leading-tight whitespace-pre-line font-semibold">
                    {highlightMatches(
                      sections[expandedSectionIndex].content,
                      searchQuery,
                      activeMatchIndex,
                      searchResults.matches,
                      expandedSectionIndex,
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          // NORMAL MODE: Show accordion
          !isLoading &&
          sectionCount > 0 && (
            <Accordion type="multiple" className="w-full">
              {sections.map((section, index) => (
                <AccordionItem
                  key={index}
                  value={`section-${index}`}
                  data-section-index={index}
                  className="border-b border-gray-200 last:border-b-0"
                >
                  <AccordionTrigger className="text-left text-xs font-semibold hover:no-underline py-3">
                    {section.title}
                  </AccordionTrigger>
                  <AccordionContent>
                    <p className="text-xs text-gray-700 whitespace-pre-line leading-tight font-semibold pb-3">
                      {section.content}
                    </p>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )
        )}
      </div>
        </>
      )}

      {/* Resize Indicator Overlay (shown during resize) */}
      {isResizing && (
        <div className="absolute inset-0 bg-blue-500/5 pointer-events-none z-10" />
      )}
    </div>
  );
};
